/**
 * `heysegment check` — report the safe-state of the session's version.
 *
 * Pure read: prints whether it's safe to push (and why not), and exits non-zero
 * when contended. The VTM runs this before push/deploy; locally it's an at-a-glance
 * "is anyone else on this version?".
 *
 * @module
 */
import { Console, Effect } from "effect"
import { Command } from "effect/unstable/cli"
import { withSession, step } from "../session/with-session"
import { OperationFailed } from "../errors"
import { SessionClient } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

export const checkCommand = Command.make(
  "check",
  { dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, (session) =>
      Effect.gen(function* () {
        const client = yield* SessionClient

        // Cross-version heads-up first: if the bound version was superseded
        // (made live / a newer draft exists), no amount of safe-state matters.
        const version = yield* step("check:version", client.getVersionStatus(void 0))
        if (version.recommendation === "switch") {
          const working =
            version.workingVersionNumber === null
              ? "a newer working version"
              : `v${version.workingVersionNumber}`
          yield* Console.log(
            `⚠ version v${version.boundVersionNumber ?? "?"} is ${version.boundStatus}${
              version.boundFrozen ? " (frozen)" : ""
            } — the current working version is ${working}.`,
          )
          yield* Console.log("  Run `heysegment switch` to move to it.")
        }

        const state = yield* step("check", client.getSafetyState(void 0))

        yield* Console.log(
          `version ${session.versionId} — revision ${state.currentRevision} (you pulled at ${state.baseRevision})`,
        )
        for (const s of state.concurrentSessions) {
          yield* Console.log(`  • other session: ${s.origin} (last seen ${s.lastSeenAt})`)
        }

        if (state.safeToPush) {
          yield* Console.log("✓ safe to push")
          return
        }

        yield* Console.error("✗ contended — not safe to push:")
        for (const reason of state.reasons) {
          yield* Console.error(`  • ${reason}`)
        }
        yield* Console.error(
          "Run `heysegment pull` to take the latest, or `heysegment acknowledge` to override.",
        )
        return yield* Effect.fail(new OperationFailed({ failed: ["contended"] }))
      }),
    ),
).pipe(Command.withDescription("Check whether it is safe to push the session's version"))
